// Custom relance type editor (US-159, US-160).
//
// One card per row in the `customTypes` array. Renders an editable label
// + when textarea + delay/attempts steppers + AI hint + delete button. The
// parent owns the persistence (via `useUpdateCustomRelanceType` /
// `useDeleteCustomRelanceType`) — this component is purely presentational.

import { useState, useEffect } from 'react';

import { useT } from '@barel/i18n';
import { FieldAssembly, IconButton, Input, Segmented, Switch, Textarea } from '@barel/ui';
import { Trash2 } from 'lucide-react';

import type { RelanceCustomTypeRow } from '@barel/api-client/react';

export interface CustomTypeEditorProps {
  type: RelanceCustomTypeRow;
  /** Persist a partial patch (label / whenText / hint / delay / attempts / enabled). */
  onPatch: (
    patch: Partial<
      Pick<
        RelanceCustomTypeRow,
        'label' | 'whenText' | 'hint' | 'delayDays' | 'maxAttempts' | 'enabled'
      >
    >,
  ) => Promise<void> | void;
  onDelete: () => Promise<void> | void;
  busy?: boolean;
}

/**
 * `CustomTypeEditor` keeps the editable fields in local state so the user
 * can type without round-tripping every keystroke; on blur (debounced via
 * the parent), the patch is flushed.
 */
export function CustomTypeEditor({ type, onPatch, onDelete, busy }: CustomTypeEditorProps) {
  const t = useT('relances');
  const [label, setLabel] = useState(type.label);
  const [whenText, setWhenText] = useState(type.whenText);
  const [hint, setHint] = useState(type.hint);

  // Re-prime if the row changes underneath us (mutation onSuccess refetch).
  useEffect(() => {
    setLabel(type.label);
    setWhenText(type.whenText);
    setHint(type.hint);
  }, [type.id, type.label, type.whenText, type.hint]);

  const flush = (
    patch: Partial<
      Pick<
        RelanceCustomTypeRow,
        'label' | 'whenText' | 'hint' | 'delayDays' | 'maxAttempts' | 'enabled'
      >
    >,
  ): void => {
    void onPatch(patch);
  };

  return (
    <div
      data-component="CustomTypeEditor"
      data-id={type.id}
      className="rounded-xl border border-default bg-bg-surface px-4 py-3"
    >
      <div className="flex items-start gap-3">
        <div
          aria-hidden
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-default bg-bg-elevated text-base"
        >
          ✦
        </div>
        <div className="min-w-0 flex-1">
          <Input
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            onBlur={() => {
              if (label !== type.label) flush({ label });
            }}
            placeholder={t('custom.labelPlaceholder')}
            disabled={busy}
            data-testid={`custom-type-${type.id}-label`}
          />
        </div>
        <IconButton
          aria-label={t('custom.deleteAriaLabel')}
          variant="ghost"
          size="md"
          onClick={() => {
            void onDelete();
          }}
          disabled={busy}
          type="button"
          data-testid={`custom-type-${type.id}-delete`}
          icon={<Trash2 size={14} aria-hidden />}
        />
        <Switch
          aria-label={t('custom.deleteAriaLabel')}
          checked={type.enabled}
          onCheckedChange={(next: boolean) => flush({ enabled: next })}
          disabled={busy}
          data-testid={`custom-type-${type.id}-switch`}
        />
      </div>

      {type.enabled ? (
        <div className="mt-3 flex flex-col gap-3 border-t border-subtle pt-3">
          <FieldAssembly label={t('types.whenLabel')}>
            <Textarea
              value={whenText}
              onChange={(event) => setWhenText(event.target.value)}
              onBlur={() => {
                if (whenText !== type.whenText) flush({ whenText });
              }}
              placeholder={t('custom.whenPlaceholder')}
              rows={2}
              disabled={busy}
              data-testid={`custom-type-${type.id}-when`}
            />
          </FieldAssembly>

          <div className="flex flex-wrap gap-4">
            <FieldAssembly label={t('types.delayLabel')}>
              <DayStepper
                value={type.delayDays}
                onChange={(v) => flush({ delayDays: v })}
                disabled={busy}
                ariaLabel={t('types.delayLabel')}
                renderValue={(v) => t('types.delayValue', { days: v })}
                testId={`custom-type-${type.id}-delay`}
              />
            </FieldAssembly>
            <FieldAssembly label={t('types.maxAttemptsLabel')}>
              <Segmented<'1' | '2'>
                value={String(type.maxAttempts) as '1' | '2'}
                onValueChange={(next) => flush({ maxAttempts: Number(next) as 1 | 2 })}
                options={[
                  { value: '1', label: t('types.maxAttempts1') },
                  { value: '2', label: t('types.maxAttempts2') },
                ]}
                aria-label={t('types.maxAttemptsLabel')}
                size="sm"
                disabled={busy}
              />
            </FieldAssembly>
          </div>

          <FieldAssembly label={t('types.aiHintLabel')}>
            <Textarea
              value={hint}
              onChange={(event) => setHint(event.target.value)}
              onBlur={() => {
                if (hint !== type.hint) flush({ hint });
              }}
              placeholder={t('types.aiHintPlaceholder')}
              rows={3}
              disabled={busy}
              data-testid={`custom-type-${type.id}-hint`}
            />
          </FieldAssembly>
        </div>
      ) : null}
    </div>
  );
}

interface DayStepperProps {
  value: number;
  onChange: (next: number) => void;
  disabled?: boolean;
  ariaLabel: string;
  renderValue: (n: number) => string;
  testId?: string;
}

function DayStepper({
  value,
  onChange,
  disabled,
  ariaLabel,
  renderValue,
  testId,
}: DayStepperProps) {
  const min = 1;
  const max = 90;
  const v = Math.max(min, Math.min(max, value || min));
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex items-center overflow-hidden rounded-md border border-strong bg-bg-elevated"
      data-testid={testId}
    >
      <IconButton
        aria-label={`${ariaLabel} —`}
        size="sm"
        variant="ghost"
        onClick={() => onChange(Math.max(min, v - 1))}
        disabled={disabled || v <= min}
        type="button"
        icon={<span aria-hidden>−</span>}
      />
      <div className="min-w-[64px] border-x border-default px-2 text-center text-xs font-semibold text-primary">
        {renderValue(v)}
      </div>
      <IconButton
        aria-label={`${ariaLabel} +`}
        size="sm"
        variant="ghost"
        onClick={() => onChange(Math.min(max, v + 1))}
        disabled={disabled || v >= max}
        type="button"
        icon={<span aria-hidden>+</span>}
      />
    </div>
  );
}
